/**
 * Verdict ranking.
 *
 * The analyst's first question in the inspector is "is this safe?" — but
 * the inspector's data model spreads the answer across five tabs
 * (envelope, auth, trust, indicators, body). This module folds the
 * already-derived spoof / auth / crypto / indicator signals into a
 * single deterministic 4-step verdict so the new `verdict-overview`
 * canvas surface can answer that question at a glance.
 *
 * Pure function: input is the full {@link EmailAnalysis}, output is a
 * {@link VerdictRanking}. No DNS, no async work, no ML — every signal
 * the ranking touches has already been computed by an upstream
 * derivation. The ranking only re-projects.
 *
 * Scoring rule (start conservative — every contribution is documented
 * inline so the analyst can see WHY a verdict landed where it did):
 *
 *  - `spoof.worst === "danger"` → start malicious
 *  - `spoof.worst === "warn"` → suspicious
 *  - `spoof.worst === "info"` → mixed
 *  - `spoof.worst === "ok"` + a verified signature (S/MIME / PGP / DKIM math) →
 *    legitimate; a relay-reported (unverifiable) auth pass alone → mixed (#24)
 *  - cryptoVerify.status === "fail" on signed → malicious
 *  - ARC fail on forwarded message → bump one tier worse
 *  - active HTML + trackers + url-mismatch → bump one tier worse
 *
 * Every contributing signal also carries a `route` (workbench entity
 * id) so the verdict surface can wire a "Drill →" link straight to the
 * responsible entity.
 */

import type { EmailAnalysis } from "../email-analysis";

/** Canonical 4-step ladder rendered by `VerdictOverviewSurface`. */
export type VerdictRank = "legitimate" | "mixed" | "suspicious" | "malicious";

/**
 * Canonical verdict-tier → tone mapping (the 4-tone `ok` / `info` / `warn` /
 * `bad` palette every verdict surface paints from). Mirrors
 * {@link verdictHeroLabel}: one source for the tier→tone projection so the
 * header pill's `data-tone` always matches the gauge.
 */
export function verdictHeroTone(rank: VerdictRank): "ok" | "info" | "warn" | "bad" {
  switch (rank) {
    case "legitimate":
      return "ok";
    case "mixed":
      return "info";
    case "suspicious":
      return "warn";
    case "malicious":
      return "bad";
  }
}

/** Direction the signal pushes the verdict — pass / fail / informational. */
export type VerdictSignalDirection = "+" | "−" | "neutral";

/**
 * One narrative signal that contributed to the verdict. The card
 * surfaces the top-3 by absolute `weight`; the "Why this verdict?"
 * expandable lists every signal with its direction + weight so the
 * analyst can defend the verdict in a write-up.
 */
export interface VerdictSignal {
  /** Stable id — keyed by the upstream source (`spoof:<id>`, `auth:<method>`, …). */
  id: string;
  /** One-line title — "DKIM fail", "Display-name impersonation", … */
  title: string;
  /** One-line detail — the upstream finding's detail or a synthesised note. */
  detail: string;
  /** Whether this signal pushes the verdict worse (-), better (+), or is informational. */
  direction: VerdictSignalDirection;
  /** Magnitude of the contribution. Higher numbers dominate the top-3 ranking. */
  weight: number;
  /**
   * Optional workbench entity id (`auth:dkim` / `trust:spoof` / …). When
   * present the card renders a "Drill →" link that navigates the
   * workbench to that entity via `requestWorkbenchEntity`.
   */
  route?: string;
}

/**
 * Output of {@link computeVerdictRanking}. Confidence is a 0-1
 * derived purely from the number of signals that fired — more
 * signals = more confident verdict.
 */
export interface VerdictRanking {
  verdict: VerdictRank;
  /** 0-1 — "how confident are we in this verdict?". */
  confidence: number;
  /** Every signal that fired, sorted strongest-first. */
  signals: VerdictSignal[];
  /**
   * Distinct authentication method names that produced a result (`spf`,
   * `dkim`, `dmarc`, `arc`, `bimi`). Surfaced in the confidence subtitle
   * so the analyst sees which methods underpin the verdict.
   */
  methods: string[];
}

// Exact result tokens that count as a HARD authentication failure. The match
// is by whole token, never by substring, because `softfail` CONTAINS `fail`:
// a substring test would promote every soft SPF failure (the routine result
// for a forwarded message) to a hard one and rank a legitimate message as
// spoofed. Softfail is a warn-level signal, handled separately below.
const FAIL_RESULTS = new Set(["fail", "hardfail", "permerror", "policy"]);
// Softer "the method ran but didn't hard-pass" tokens. These push a small
// warn-weight negative signal WITHOUT bumping the verdict tier.
const WARN_RESULTS = new Set(["softfail", "temperror"]);

function isFailResult(result: string): boolean {
  return FAIL_RESULTS.has(result.trim().toLowerCase());
}

function isWarnResult(result: string): boolean {
  return WARN_RESULTS.has(result.trim().toLowerCase());
}

function isPassResult(result: string): boolean {
  const lower = result.toLowerCase();
  return /\bpass\b/.test(lower);
}

/**
 * Walk the 4-step ladder one notch toward `malicious`. Bottom (legitimate)
 * → mixed → suspicious → malicious. A `bumpDown` from `malicious` is a
 * no-op — the verdict is already at the floor.
 */
function bumpDown(rank: VerdictRank): VerdictRank {
  if (rank === "legitimate") return "mixed";
  if (rank === "mixed") return "suspicious";
  if (rank === "suspicious") return "malicious";
  return "malicious";
}

/**
 * Map `analysis.spoof.worst` to the initial verdict tier. The spoof
 * deriver already folds every textbook spoofing signal (display-name
 * impersonation, reply-to divergence, return-path mismatch,
 * suspicious-domain heuristics) into a single ranked verdict — we read
 * its `worst` field as the anchor and let the downstream contributors
 * bump it.
 */
function spoofWorstToRank(
  worst: EmailAnalysis["spoof"]["worst"],
  hasVerifiedSignature: boolean,
): VerdictRank {
  if (worst === "danger") return "malicious";
  if (worst === "warn") return "suspicious";
  if (worst === "info") return "mixed";
  // `ok` — only graduate to "legitimate" when the inspector's OWN crypto math
  // verified a signature (S/MIME / PGP / DKIM). A relay-reported
  // Authentication-Results pass is unverifiable here and forgeable, so a clean
  // report backed only by it stays at "mixed": the verdict never reaches green
  // on a signal we could not confirm (#24). Clean mail with no verified
  // signature (the common parse-only case) also stays "mixed" rather than
  // over-confident.
  return hasVerifiedSignature ? "legitimate" : "mixed";
}

/**
 * Pure ranking function. Inputs the entire analysis (cheap — every
 * upstream derivation has already run); outputs the verdict tier,
 * confidence, ranked signals, and the set of auth methods that
 * produced a result.
 */
export function computeVerdictRanking(analysis: EmailAnalysis): VerdictRanking {
  const signals: VerdictSignal[] = [];
  const methodsSet = new Set<string>();

  const authEntries = analysis.auth.flatMap((row) => row.entries);
  for (const entry of authEntries) {
    methodsSet.add(entry.method.toLowerCase());
  }
  // De-dupe per-method auth verdicts so a message that ships two
  // Authentication-Results headers (e.g. one from the gateway, one from
  // the inbox provider) doesn't surface the same `method:pass` /
  // `method:fail` signal twice. The first observed entry per method+verdict
  // pair wins. Stable insertion order; we track via Set<string>.
  const seenAuthSignals = new Set<string>();

  // Anchor: spoof report's worst severity. The deriver pre-folds every
  // textbook spoofing signal so we don't need to second-guess it.
  //
  // Trust boundary (#24): a clean spoof report graduates to the top
  // "legitimate" tier ONLY when the inspector's OWN verification math confirmed
  // a signature — a passing S/MIME or PGP signature, or a DKIM whose body-hash
  // AND signature both verified in-tree. A relay-reported Authentication-Results
  // `pass` is NOT enough: it is unverifiable here (no DNS, no trusted
  // authserv-id — RFC 8601) and an attacker can forge it, so on its own it
  // leaves the verdict at "mixed", never the green of "legitimate". In
  // parse-only mode nothing is verified, so clean mail honestly reads "mixed"
  // until the crypto/DNS verifier runs. Mirrors the per-surface `reported`
  // treatment (SpoofCard / AuthEvidenceGrid / auth passport).
  const hasVerifiedSignature =
    analysis.layers.crypto.secureParts.some((p) => {
      const v = p.cryptoVerify;
      if (v?.status !== "pass") return false;
      // A mathematically-valid signature whose signer identity MISMATCHES the
      // From address is impersonation, not a legitimacy signal — exclude it.
      return v.identityMatch !== "mismatch";
    }) ||
    analysis.dkimSignatures.some((s) => {
      const v = s.cryptoVerify;
      if (!v) return false;
      return v.bodyHash.status === "pass" && v.signature.status === "pass";
    });
  let rank: VerdictRank = spoofWorstToRank(analysis.spoof.worst, hasVerifiedSignature);

  // ---- Spoof findings: the deriver already sorted by severity. Pull the
  // strongest contributors so the card's top-3 reflects what the spoof report
  // saw. We weight by severity so a danger finding dominates a warn finding
  // even if both share a title.
  for (const finding of analysis.spoof.findings) {
    const weight =
      finding.severity === "danger"
        ? 100
        : finding.severity === "warn"
          ? 60
          : finding.severity === "info"
            ? 25
            : 5;
    signals.push({
      id: `spoof:${finding.id}`,
      title: finding.title,
      detail: finding.detail,
      direction: finding.severity === "ok" ? "neutral" : "−",
      weight,
      route: "trust:spoof",
    });
  }

  // ---- Authentication: each fail bumps down a tier, each pass nudges
  // confidence up (it stays a +signal in the list so the analyst can see WHY
  // the verdict didn't drop further).
  for (const entry of authEntries) {
    const method = entry.method.toLowerCase();
    const route = `auth:${method}`;
    if (isFailResult(entry.result)) {
      // SPF / DKIM / DMARC / ARC / BIMI hard-fail is a strong tier
      // contribution; the verdict deserves at least one notch down per
      // failing method. The de-dupe Set guards against the same
      // failing method being seen twice (two AR headers); a duplicate
      // verdict shouldn't bump the rank twice.
      const sigId = `auth:${method}:fail`;
      if (seenAuthSignals.has(sigId)) continue;
      seenAuthSignals.add(sigId);
      rank = bumpDown(rank);
      signals.push({
        id: sigId,
        title: `${method.toUpperCase()} ${entry.result}`,
        detail: entry.comment || (entry.domain ? `domain=${entry.domain}` : entry.raw),
        direction: "−",
        weight: 80,
        route,
      });
    } else if (isWarnResult(entry.result)) {
      // SPF softfail / DKIM-or-DMARC temperror is a soft signal — the
      // method declined to hard-pass but didn't hard-fail either. Surface
      // it as a warn-weight negative contributor (mirrors the 'warn'
      // semantics elsewhere) WITHOUT bumping the verdict tier.
      const sigId = `auth:${method}:warn`;
      if (seenAuthSignals.has(sigId)) continue;
      seenAuthSignals.add(sigId);
      signals.push({
        id: sigId,
        title: `${method.toUpperCase()} ${entry.result}`,
        detail: entry.comment || (entry.domain ? `domain=${entry.domain}` : entry.raw),
        direction: "−",
        weight: 30,
        route,
      });
    } else if (isPassResult(entry.result)) {
      const sigId = `auth:${method}:pass`;
      if (seenAuthSignals.has(sigId)) continue;
      seenAuthSignals.add(sigId);
      signals.push({
        id: sigId,
        title: `${method.toUpperCase()} pass`,
        detail: entry.domain ? `domain=${entry.domain}` : entry.raw,
        direction: "+",
        weight: 40,
        route,
      });
    }
  }

  // ---- Cryptographic signature math: a `fail` verdict on a signed part is
  // one of the strongest "this message was tampered with" signals the
  // inspector ships. Drop straight to malicious.
  const secureParts = analysis.layers.crypto.secureParts;
  for (const part of secureParts) {
    const verify = part.cryptoVerify;
    if (!verify) continue;
    if (verify.status === "fail") {
      rank = "malicious";
      signals.push({
        id: `crypto:${part.kind}:fail`,
        title: `${part.kind} signature failed`,
        detail: verify.detail,
        direction: "−",
        weight: 120,
        route: "trust:crypto",
      });
    } else if (verify.status === "pass") {
      signals.push({
        id: `crypto:${part.kind}:pass`,
        title: `${part.kind} signature verified`,
        detail: verify.detail ?? "Signature math passed",
        direction: "+",
        weight: 50,
        route: "trust:crypto",
      });
    }
    // ---- Signer-identity mismatch (Wave 3). DELIBERATELY orthogonal to the
    // math verdict: a mathematically-VALID signature whose signer cert email
    // doesn't match the From: address is a textbook impersonation signal — an
    // attacker signs with their own valid cert while spoofing the From line.
    // `identityMatch` rides on the `pass` / `fail` variants only (never on
    // `skipped`, which we've already `continue`d past above). Surface it as a
    // strong warn-weight negative contributor so the spoof actually moves the
    // verdict instead of only lighting up the pill.
    if (verify.status !== "skipped" && verify.identityMatch === "mismatch") {
      rank = bumpDown(rank);
      signals.push({
        id: `crypto:${part.kind}:signer-mismatch`,
        title: `${part.kind} signer identity mismatch`,
        detail:
          verify.signerIdentity != null
            ? `Signer cert identity (${verify.signerIdentity}) does not match the From address.`
            : "Signer cert identity does not match the From address.",
        direction: "−",
        weight: 95,
        route: "trust:crypto",
      });
    }
  }
  // DKIM body-hash / signature math verdicts ride on each DkimSignature.
  for (const [i, sig] of analysis.dkimSignatures.entries()) {
    const verify = sig.cryptoVerify;
    if (!verify) continue;
    if (verify.signature.status === "fail" || verify.bodyHash.status === "fail") {
      rank = "malicious";
      signals.push({
        // PII-safe id: keyed by signature INDEX, NEVER the d= signing domain.
        // This id is forwarded verbatim in the "Open with Claude" handoff
        // payload (claude-handoff.ts → signalIds), whose contract is that no
        // message-derived domain travels raw — the raw d= host previously
        // leaked here next to a "nothing identifying travels" note. The domain
        // is still shown in the local title/detail, which the handoff omits.
        id: `dkim:${i}:fail`,
        title: "DKIM signature math failed",
        detail:
          verify.signature.status === "fail"
            ? verify.signature.detail
            : verify.bodyHash.status === "fail"
              ? verify.bodyHash.detail
              : "Crypto verify failed",
        direction: "−",
        weight: 110,
        route: "auth:dkim",
      });
    }
  }

  // ---- ARC failure on a forwarded message. The ARC chain only matters when
  // a forwarder rewrote auth results; if any ARC entry reports cv=fail /
  // cv=invalid the chain is broken and the "forwarded by trusted relay" claim
  // is untrustworthy.
  const arcChain = analysis.arcChain;
  if (arcChain.length > 0) {
    const broken = arcChain.some(
      (instance) => instance.cv === "fail" || instance.cv === "invalid",
    );
    if (broken) {
      rank = bumpDown(rank);
      signals.push({
        id: "auth:arc:broken",
        title: "ARC chain broken",
        detail: "A forwarder along the path declared cv=fail or cv=invalid.",
        direction: "−",
        weight: 70,
        route: "auth:arc",
      });
    }
  }

  // ---- Active HTML + tracking pixels + url-mismatch together is a classic
  // phishing fingerprint. Each on its own is information; together they bump
  // the verdict one tier worse.
  const htmlRisks = analysis.local.htmlRisks;
  const activeHtmlCount =
    htmlRisks.formCount +
    htmlRisks.iframeCount +
    htmlRisks.objectEmbedCount +
    htmlRisks.metaRefreshCount +
    htmlRisks.inlineEventHandlerCount;
  const hasActiveHtml = activeHtmlCount > 0;
  const trackingPixels = analysis.linkStats.trackingPixels;
  const mismatched = analysis.linkStats.mismatched;
  const phishingTriad = hasActiveHtml && trackingPixels > 0 && mismatched > 0;
  if (phishingTriad) {
    rank = bumpDown(rank);
    signals.push({
      id: "content:phishing-triad",
      title: "Active HTML + trackers + URL mismatches",
      detail:
        `${activeHtmlCount} active element(s), ${trackingPixels} tracker(s), ` +
        `${mismatched} link(s) where visible text disagrees with the href.`,
      direction: "−",
      weight: 65,
      route: "trust:active-html",
    });
  } else {
    // Surface the individual signals as info contributors so the analyst
    // can see them in the "Why this verdict?" expandable even when they
    // didn't trigger the triad bump.
    if (hasActiveHtml) {
      signals.push({
        id: "content:active-html",
        title: "Active HTML elements present",
        detail: `${activeHtmlCount} script/iframe/form/meta-refresh element(s).`,
        direction: "−",
        weight: 30,
        route: "trust:active-html",
      });
    }
    if (mismatched > 0) {
      signals.push({
        id: "content:url-mismatch",
        title: "Link text disagrees with destination",
        detail: `${mismatched} link(s) where the visible text doesn't match the href.`,
        direction: "−",
        weight: 35,
        route: "body:links",
      });
    }
    if (trackingPixels > 0) {
      signals.push({
        id: "content:trackers",
        title: `${trackingPixels} tracking pixel${trackingPixels === 1 ? "" : "s"}`,
        detail: "Open-tracking pixel(s) detected in the rendered body.",
        direction: "−",
        weight: 15,
        route: "trust:content",
      });
    }
  }

  // ---- Known-bad indicator hits. A `hash` indicator is a denylist hit ONLY
  // when its `knownBad` flag is set — `collectHashes()` sets it by running the
  // sha-256 through `isKnownBadSha256()`. We must NOT key off `filename`: that
  // field is populated on EVERY per-attachment (and whole-message) hash row,
  // so filtering on it would fire on any message carrying attachment hashes.
  const knownBadHits = analysis.indicators.filter(
    (i) => i.kind === "hash" && i.knownBad === true,
  );
  if (knownBadHits.length > 0) {
    rank = bumpDown(rank);
    signals.push({
      id: "indicators:known-bad",
      title: `${knownBadHits.length} known-bad indicator hit${knownBadHits.length === 1 ? "" : "s"}`,
      detail: knownBadHits
        .slice(0, 3)
        .map((i) => i.filename ?? i.value)
        .join(", "),
      direction: "−",
      weight: 90,
      route: "indicators:overview",
    });
  }

  // ---- Sort signals by absolute weight, fail signals first when tied so the
  // card's top-3 leans toward the actionable signals.
  signals.sort((a, b) => {
    if (b.weight !== a.weight) return b.weight - a.weight;
    if (a.direction === "−" && b.direction !== "−") return -1;
    if (b.direction === "−" && a.direction !== "−") return 1;
    return a.id.localeCompare(b.id);
  });

  // Wave 46 follow-up: the per-source dedupe sets above protect against
  // a method being seen twice in the SAME source (e.g. two AR headers
  // both reporting dmarc=fail), but spoof findings + auth entries can
  // produce signals whose `title + detail` text reads identically even
  // when their `id` differs (e.g. a spoof finding titled "DMARC fail"
  // co-existing with the auth-derived auth:dmarc:fail). The verdict
  // canvas top-3 shouldn't render the same line three times — collapse
  // by id first, then by visible (title, detail) pair, keeping the
  // first occurrence (which is the highest-priority by collection
  // order: spoof → auth → crypto → arc → content).
  const dedupedSignals: VerdictSignal[] = [];
  const seenIds = new Set<string>();
  const seenTextPairs = new Set<string>();
  for (const sig of signals) {
    if (seenIds.has(sig.id)) continue;
    const textKey = `${sig.title}\u0000${sig.detail}`;
    if (seenTextPairs.has(textKey)) continue;
    seenIds.add(sig.id);
    seenTextPairs.add(textKey);
    dedupedSignals.push(sig);
  }

  // Confidence: log-scale on the number of signals that fired, capped
  // at 1. 0 signals → 0; 1 signal → ~0.33; 3 signals → ~0.66; 7+
  // signals → ~1. The point of the band isn't statistical rigour — it
  // tells the analyst "we have evidence" vs "we're guessing".
  const confidence =
    dedupedSignals.length === 0
      ? 0
      : Math.min(1, Math.log2(1 + dedupedSignals.length) / 3);

  // Stable method ordering for the subtitle: canonical SPF / DKIM /
  // DMARC / ARC / BIMI first, then whatever else appeared.
  const canonical = ["spf", "dkim", "dmarc", "arc", "bimi"];
  const ordered: string[] = [];
  for (const m of canonical) {
    if (methodsSet.has(m)) ordered.push(m);
  }
  for (const m of methodsSet) {
    if (!canonical.includes(m)) ordered.push(m);
  }

  return {
    verdict: rank,
    confidence,
    signals: dedupedSignals,
    methods: ordered,
  };
}
